// File: src/pages/Home/Home.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Zap, 
  Star, 
  DollarSign, 
  TrendingUp, 
  MapPin, 
  AlertCircle, 
  CheckCircle,
  Upload,
  Shield,
  Clock,
  Users
} from 'lucide-react';
import UploadZone from '../../components/Upload/UploadZone';
import styles from './Home.module.css';

const Home = () => {
  const navigate = useNavigate();
  const [uploadProgress, setUploadProgress] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Demo stats for social proof
  const [stats] = useState({
    policiesAnalyzed: '47,329',
    savedThisMonth: '$2.3M',
    avgSavings: '$540',
    usersOnline: '1,247'
  });

  // Sample results for the demo section
  const sampleResults = [
    {
      id: 1,
      grade: 'D-',
      type: 'Auto Insurance',
      location: 'Toronto, ON',
      overpaying: '+34%',
      savings: '$540',
      message: 'Getting CRUSHED!',
      color: 'var(--color-grade-d-minus)',
      bgColor: 'rgba(185, 28, 28, 0.1)'
    },
    {
      id: 2,
      grade: 'A+',
      type: 'Home Insurance',
      location: 'Vancouver, BC',
      overpaying: '-18%',
      savings: '$450',
      message: 'CRUSHING it!',
      color: 'var(--color-grade-a-plus)',
      bgColor: 'rgba(0, 255, 136, 0.1)'
    },
    {
      id: 3,
      grade: 'C',
      type: 'Condo Insurance',
      location: 'Calgary, AB',
      overpaying: '+12%',
      savings: '$180',
      message: 'Room for improvement',
      color: 'var(--color-grade-c)',
      bgColor: 'rgba(241, 123, 44, 0.1)'
    }
  ];

  const handleFileUpload = async (file) => {
    try {
      setIsProcessing(true);
      setUploadProgress(0);
      
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);
      
      // Simulate API call to upload file
      const formData = new FormData();
      formData.append('policy', file);
      
      // Replace this with your actual API endpoint
      const response = await fetch('/api/upload-policy', {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error('Upload failed');
      }
      
      const result = await response.json();
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      // Navigate to analysis page
      setTimeout(() => {
        navigate(`/analysis?id=${result.id || 'demo'}`);
      }, 1000);
      
    } catch (error) {
      console.error('Upload error:', error);
      setUploadProgress(null);
      setIsProcessing(false);
      // Show error notification
      alert('Upload failed. Please try again.');
    }
  };

  const scrollToUpload = () => {
    document.getElementById('upload-section')?.scrollIntoView({ 
      behavior: 'smooth' 
    });
  };

  return (
    <div className={styles.homePage}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        {/* Background Effects */}
        <div className={styles.backgroundEffects}>
          <div className={styles.floatingElement1}></div>
          <div className={styles.floatingElement2}></div>
          <div className={styles.floatingElement3}></div>
        </div>

        <div className={styles.heroContent}>
          <div className={styles.heroText}>
            <h1 className={styles.title}>
              Are You Getting
              <span className={styles.titleAccent}>CRUSHED</span>
              by Your Insurance?
            </h1>
            
            <p className={styles.subtitle}>
              Upload your policy. Get your grade. See what your neighbors pay.
              <br />
              <strong className={styles.subtitleAccent}>Time to crush those overpriced premiums!</strong>
            </p>

            {/* Social Proof */}
            <div className={styles.socialProof}>
              <div className={styles.proofItem}>
                <Star className={styles.proofIcon} />
                <span>{stats.policiesAnalyzed} policies analyzed</span>
              </div>
              <div className={styles.proofItem}>
                <DollarSign className={styles.proofIcon} />
                <span>{stats.savedThisMonth} saved this month</span>
              </div>
              <div className={styles.proofItem}>
                <Users className={styles.proofIcon} />
                <span>{stats.usersOnline} users online now</span>
              </div>
            </div>

            <button className={styles.ctaButton} onClick={scrollToUpload}>
              <Upload size={20} />
              Crush My Rates Now!
            </button>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className={styles.scrollIndicator}>
          <div className={styles.scrollMouse}>
            <div className={styles.scrollWheel}></div>
          </div>
          <span className={styles.scrollText}>See how it works</span>
        </div>
      </section>

      {/* Upload Section */}
      <section id="upload-section" className={styles.uploadSection}>
        <div className={styles.container}>
          <div className={styles.uploadContent}>
            <h2 className={styles.sectionTitle}>
              Ready to Discover the Truth?
            </h2>
            <p className={styles.sectionSubtitle}>
              Upload your insurance policy and get your brutal honesty grade in 30 seconds
            </p>
            
            <UploadZone 
              onFileUpload={handleFileUpload}
              isProcessing={isProcessing}
              className={styles.uploadZone}
            />
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className={styles.howItWorksSection}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>How Cover Crush Works</h2>
          <div className={styles.stepsGrid}>
            <div className={styles.step}>
              <div className={styles.stepIcon}>
                <Upload />
              </div>
              <h3 className={styles.stepTitle}>1. Upload Your Policy</h3>
              <p className={styles.stepDescription}>
                Drop your insurance document - PDF, photo, whatever. We'll scan every detail.
              </p>
            </div>
            
            <div className={styles.step}>
              <div className={styles.stepIcon}>
                <TrendingUp />
              </div>
              <h3 className={styles.stepTitle}>2. AI Analysis</h3>
              <p className={styles.stepDescription}>
                Our AI compares your rates against {stats.policiesAnalyzed}+ real policies in your area.
              </p>
            </div>
            
            <div className={styles.step}>
              <div className={styles.stepIcon}>
                <MapPin />
              </div>
              <h3 className={styles.stepTitle}>3. Get Your Grade</h3>
              <p className={styles.stepDescription}>
                Receive an A-F grade and see exactly how much you're overpaying (or saving!).
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sample Results Section */}
      <section id="examples" className={styles.examplesSection}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>Real Results from Real People</h2>
          <p className={styles.sectionSubtitle}>
            See what others discovered about their insurance
          </p>
          
          <div className={styles.resultsGrid}>
            {sampleResults.map((result) => (
              <div 
                key={result.id}
                className={styles.resultCard}
                style={{ 
                  background: result.bgColor,
                  borderColor: result.color
                }}
              >
                <div className={styles.resultHeader}>
                  <div 
                    className={styles.resultGrade}
                    style={{ color: result.color }}
                  >
                    {result.grade}
                  </div>
                  <div className={styles.resultInfo}>
                    <div className={styles.resultType}>{result.type}</div>
                    <div className={styles.resultLocation}>
                      <MapPin size={12} />
                      {result.location}
                    </div>
                  </div>
                </div>
                
                <div className={styles.resultMessage}>{result.message}</div>
                
                <div className={styles.resultStats}>
                  <div className={styles.resultStat}>
                    <span className={styles.statLabel}>vs Average:</span>
                    <span 
                      className={`${styles.statValue} ${result.overpaying.startsWith('+') ? styles.negative : styles.positive}`}
                    >
                      {result.overpaying}
                    </span>
                  </div>
                  <div className={styles.resultStat}>
                    <span className={styles.statLabel}>Potential Savings:</span>
                    <span className={styles.statValue}>{result.savings}/year</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className={styles.trustSection}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>Why Trust Cover Crush?</h2>
          
          <div className={styles.trustGrid}>
            <div className={styles.trustItem}>
              <Shield className={styles.trustIcon} />
              <h3 className={styles.trustTitle}>Bank-Level Security</h3>
              <p className={styles.trustDescription}>
                Your documents are encrypted and automatically deleted after analysis.
              </p>
            </div>
            
            <div className={styles.trustItem}>
              <Clock className={styles.trustIcon} />
              <h3 className={styles.trustTitle}>Lightning Fast</h3>
              <p className={styles.trustDescription}>
                Get results in 30 seconds. No waiting around for quotes.
              </p>
            </div>
            
            <div className={styles.trustItem}>
              <CheckCircle className={styles.trustIcon} />
              <h3 className={styles.trustTitle}>100% Free</h3>
              <p className={styles.trustDescription}>
                No hidden fees, no spam, no BS. Just honest insurance analysis.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className={styles.finalCTA}>
        <div className={styles.container}>
          <div className={styles.ctaContent}>
            <h2 className={styles.ctaTitle}>
              Ready to Crush Your Insurance Costs?
            </h2>
            <p className={styles.ctaSubtitle}>
              Join {stats.policiesAnalyzed}+ people who discovered the truth about their premiums
            </p>
            <button className={styles.ctaButton} onClick={scrollToUpload}>
              <Upload size={20} />
              Upload Policy & Get Crushed
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;